import { GameManager } from "../GameManager";
import { GameVars } from "../GameVars";

export class PreloadScene extends Phaser.Scene {

    private progressBar: Phaser.GameObjects.Graphics;
    private scaledItemsContainer: Phaser.GameObjects.Container;
    private backgroundCover?: Phaser.GameObjects.Rectangle;
    private realProgress = 0;
    private visualProgress = 0;
    private logoAnimationCompleted = false;
    private assetsLoaded = false;
    private sceneReady = false;
    private transitionStarted = false;

    constructor() {

        super("PreloadScene");
    }

    public preload(): void {

        this.composeScene();
        this.loadAssets();
    }

    public create(): void {

        GameManager.setCurrentScene(this);
        this.sceneReady = true;
        this.tryStartTransition();
    }

    private composeScene(): void {

        this.add.text(-100, -100, "abcdefg", { fontFamily: "Baloo2-Bold", fontSize: "28px", color: "#FFFFFF", align: "center" });
        this.add.text(-100, -100, "abcdefg", { fontFamily: "Baloo2-Regular", fontSize: "28px", color: "#FFFFFF", align: "center" });
        this.add.text(-100, -100, "abcdefg", { fontFamily: "Baloo2-SemiBold", fontSize: "28px", color: "#FFFFFF", align: "center" });

        const background = new Phaser.GameObjects.Rectangle(this, 0, 0, GameVars.gameWidth, GameVars.gameHeight, 0x03142E);
        background.setOrigin(0);
        this.add.existing(background);

        this.backgroundCover = new Phaser.GameObjects.Rectangle(this, 0, 0, GameVars.gameWidth, GameVars.gameHeight, 0x03142E);
        this.backgroundCover.setOrigin(0);
        this.add.existing(this.backgroundCover);

        this.scaledItemsContainer = this.add.container(GameVars.gameWidth / 2, GameVars.gameHeight / 2);
        this.scaledItemsContainer.scaleY = GameVars.scaleY;

        this.addLogo();
        this.addProgressBar();

        this.events.on(Phaser.Scenes.Events.UPDATE, this.updateVisualProgress, this);
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);
    }

    private addProgressBar(): void {

        const barWidth = 350;
        const barHeight = 10;
        const borderWidth = 3;
        const progressContainer = new Phaser.GameObjects.Container(this, 0, 35);
        this.scaledItemsContainer.add(progressContainer);

        const shadow = new Phaser.GameObjects.Graphics(this);
        shadow.fillStyle(0x000000, .25);
        shadow.fillRect(-barWidth / 2 - borderWidth + 2, -borderWidth + 2, barWidth + borderWidth * 2, barHeight + borderWidth * 2);
        progressContainer.add(shadow);

        const border = new Phaser.GameObjects.Graphics(this);
        border.fillStyle(0x6e4712, 1);
        border.fillRect(-barWidth / 2 - borderWidth, -borderWidth, barWidth + borderWidth * 2, barHeight + borderWidth * 2);
        progressContainer.add(border);

        const barBackground = new Phaser.GameObjects.Graphics(this);
        barBackground.fillStyle(0x4a2e05, 1);
        barBackground.fillRect(-barWidth / 2, 0, barWidth, barHeight);
        progressContainer.add(barBackground);

        this.progressBar = new Phaser.GameObjects.Graphics(this);
        this.progressBar.fillGradientStyle(0xfffde6, 0xfffde6, 0xcd9a34, 0x76470d, 1);
        this.progressBar.fillRect(0, 0, barWidth, barHeight);
        this.progressBar.x = -barWidth / 2;
        this.progressBar.scaleX = 0;
        progressContainer.add(this.progressBar);
    }

    private addLogo(): void {

        const logo = new Phaser.GameObjects.Image(this, 0, -155, "catafractum-logo");
        this.scaledItemsContainer.add(logo);

        const width = logo.width;
        const height = logo.height;
        const overlayKey = `logo-overlay-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
        const overlayTexture = this.textures.createCanvas(overlayKey, width, 2 * height);
        const context = overlayTexture.getContext();
        const rgb = Phaser.Display.Color.IntegerToRGB(0x03142E);
        const gradient = context.createLinearGradient(0, 0, 0, height);

        gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`);
        gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`);
        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height);
        context.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
        context.fillRect(0, height, width, height);
        overlayTexture.refresh();

        const overlay = new Phaser.GameObjects.Image(this, 0, -155 - height / 2, overlayKey);
        this.scaledItemsContainer.add(overlay);

        this.tweens.add({
            targets: overlay,
            y: overlay.y + 2 * height,
            duration: 2000,
            ease: Phaser.Math.Easing.Cubic.Out,
            onComplete: () => {
                overlay.destroy();
                this.logoAnimationCompleted = true;
                this.tryStartTransition();
            },
            callbackScope: this
        });
    }

    private loadAssets(): void {

        const cacheBuster = `?v=${Date.now()}`;

        this.load.image("splash_background", `${GameVars.isLandscape ? "assets/splash-background-landscape.jpg" : "assets/splash-background-portrait.jpg"}${cacheBuster}`);
      
        this.load.image("bg_1", "assets/bg_01.jpg");
        this.load.image("bg_1_portrait", "assets/bg_01_portrait.jpg");
        this.load.atlas("texture_atlas_1", "assets/texture_atlas_1.png", "assets/texture_atlas_1.json");
        this.load.atlas("avatar_atlas", "assets/avatar_atlas.png", "assets/avatar_atlas.json");
        this.load.audioSprite("audiosprite", "assets/audio/audiosprite.json", ["assets/audio/audiosprite.mp3"]);

        this.load.on("progress", this.updateLoadedPercentage, this);
        this.load.once("complete", this.onLoadComplete, this);
    }

    private updateLoadedPercentage(percentageLoaded: number): void {

        this.realProgress = Math.max(this.realProgress, percentageLoaded);
    }

    private onLoadComplete(): void {

        this.assetsLoaded = true;
        this.realProgress = 1;
        this.tryStartTransition();
    }

    private updateVisualProgress(): void {

        const target = Phaser.Math.SmoothStep(Phaser.Math.Clamp(this.realProgress, 0, 1), 0, 1);
        this.visualProgress += (target - this.visualProgress) * .18;

        if (Math.abs(target - this.visualProgress) < .0015) {
            this.visualProgress = target;
        }

        this.progressBar.scaleX = Phaser.Math.Clamp(this.visualProgress, 0, 1);
    }

    private tryStartTransition(): void {

        if (this.transitionStarted || !this.sceneReady || !this.assetsLoaded || !this.logoAnimationCompleted) {
            return;
        }

        this.transitionStarted = true;
        this.tweens.add({
            targets: [this.scaledItemsContainer, this.backgroundCover],
            alpha: 0,
            duration: 450,
            ease: Phaser.Math.Easing.Cubic.Out,
            onComplete: () => GameManager.onGameAssetsLoaded()
        });
    }

    private onShutdown(): void {

        this.events.off(Phaser.Scenes.Events.UPDATE, this.updateVisualProgress, this);
    }
}
